// src/components/Multiplayer/CustomRoomLobby.jsx
// Waiting lobby for a Custom Room: shows the shareable code (with copy button),
// the player list, a manual "Start Game" button (creator only) and voice chat
// controls (mic / speaker, both muted by default).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import './CustomRoomLobby.css';

const CustomRoomLobby = () => {
  const { code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket, isConnected, emitWithPromise } = useWebSocket();
  const { currentUser } = useAuth();

  const [room, setRoom] = useState(location.state?.room || null);
  const [isCreator, setIsCreator] = useState(location.state?.isCreator || false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // ---- voice state (mic muted by default; speaker ON so you can hear others) ----
  const [micOn, setMicOn] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const localStreamRef = useRef(null);
  const peersRef = useRef(new Map());
  const audioRef = useRef(new Map());

  // ---- copy code ----
  const copyCode = useCallback(() => {
    const text = room?.roomCode || code;
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setTimeout(() => setCopied(false), 2000);
  }, [room, code]);

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    setCopied(true);
  };

  // Open the system app-settings so the user can grant mic permission in place.
  const openAppSettings = () => {
    // 1) Native Capacitor App plugin (works when @capacitor/app is installed).
    const capApp = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App;
    if (capApp && typeof capApp.openAppSettings === 'function') {
      capApp.openAppSettings().catch(() => {});
      return;
    }
    // 2) Android WebView deep-link to the app's settings page.
    if (/Android/i.test(navigator.userAgent)) {
      try { window.location.href = 'app-settings:'; } catch (e) { /* ignore */ }
      // Deep-link may not be handled; always give manual guidance too.
      alert('Please allow Microphone access:\nSettings → Apps → JV Games → Permissions → Microphone');
      return;
    }
    // 3) iOS / desktop browsers: point the user to the right place.
    alert('Please enable Microphone access in your browser/device settings, then come back and tap the mic again.');
  };

  // ---- refresh room info ----
  const refreshRoom = useCallback(async () => {
    try {
      const res = await emitWithPromise('get-custom-room', { code }, 10000);
      if (res && res.success && res.room) {
        setRoom(res.room);
        setIsCreator(String(res.room.creatorId) === String(currentUser?.id));
      }
    } catch (e) {
      // ignore transient errors
    }
  }, [code, emitWithPromise, currentUser]);

  // ---- game start navigation ----
  const goToGame = useCallback((gameId) => {
    navigate(`/game/${gameId}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!socket) return;

    const onUpdated = (data) => {
      if (data && data.success && data.room) {
        setRoom(data.room);
        setIsCreator(String(data.room.creatorId) === String(currentUser?.id));
      }
    };
    const onStarted = (data) => {
      if (data && (data.gameId || data.room?.id)) {
        goToGame(data.gameId || data.room.id);
      }
    };
    const onSeating = (data) => {
      if (data && data.gameId) goToGame(data.gameId);
    };
    const onLeft = (data) => {
      refreshRoom();
    };

    socket.on('custom-room-updated', onUpdated);
    socket.on('custom-room-started', onStarted);
    socket.on('seating-phase-started', onSeating);
    socket.on('custom-player-left', onLeft);

    refreshRoom();

    return () => {
      socket.off('custom-room-updated', onUpdated);
      socket.off('custom-room-started', onStarted);
      socket.off('seating-phase-started', onSeating);
      socket.off('custom-player-left', onLeft);
    };
  }, [socket, refreshRoom, goToGame, currentUser]);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true); setError('');
    if (!isConnected || !socket?.connected) {
      setError('Not connected to game server. Please wait for reconnect and try again.');
      setStarting(false);
      return;
    }
    try {
      const res = await emitWithPromise('start-custom-room', { code }, 15000);
      if (res && res.success && res.gameId) {
        goToGame(res.gameId);
      } else {
        setError(res?.error || 'Failed to start game');
      }
    } catch (e) {
      const msg = e.message || 'Failed to start game';
      setError((msg.includes('Socket not connected') || msg.includes('socket'))
        ? 'Not connected to game server. Please wait for reconnect and try again.'
        : msg);
    } finally {
      setStarting(false);
    }
  };

  const handleLeave = () => {
    if (socket) socket.emit('leave-custom-room', { code });
    navigate('/multiplayer');
  };


  // ==================== VOICE CHAT ====================
  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = stream;
    stream.getAudioTracks().forEach(t => (t.enabled = false));
    return stream;
  }, []);

  const setMicEnabled = useCallback(async (enable) => {
    try {
      // 🔐 getUserMedia() is only called here → the mic permission prompt
      // appears the FIRST time the user unmutes, not when the lobby loads.
      const stream = await ensureLocalStream();
      stream.getAudioTracks().forEach(t => (t.enabled = enable));
      peersRef.current.forEach((entry) => {
        const { pc } = entry;
        stream.getAudioTracks().forEach(t => {
          const sender = pc.getSenders().find(s => s.track === t);
          if (enable && !sender) pc.addTrack(t, stream);
          else if (!enable && sender) pc.removeTrack(sender);
        });
      });
      setMicOn(enable);
      setPermissionDenied(false);
      // Unmuting the mic is a user gesture — retry remote audio playback (also unlocks autoplay).
      if (enable) {
        audioRef.current.forEach(el => { el.muted = !speakerOn; el.play().catch(() => {}); });
      }
      socket?.emit('custom-voice-state', { code, micEnabled: enable, speakerEnabled: speakerOn });
    } catch (e) {
      setPermissionDenied(true);
      setError('Microphone access denied. Please allow mic permission.');
    }
  }, [ensureLocalStream, socket, code, speakerOn]);

  const setSpeakerEnabled = useCallback((enable) => {
    setSpeakerOn(enable);
    audioRef.current.forEach(el => {
      el.muted = !enable;
      if (enable) el.play().catch(() => {});
    });
    socket?.emit('custom-voice-state', { code, micEnabled: micOn, speakerEnabled: enable });
  }, [socket, code, micOn]);

  const sendSignal = useCallback((targetId, signal) => {
    console.log('[voice] sendSignal →', targetId, signal?.type);
    socket?.emit('custom-voice-signal', { code, targetId, signal });
  }, [socket, code]);

  const setupPeer = useCallback((peerId) => {
    const key = String(peerId);
    if (peersRef.current.has(key)) return peersRef.current.get(key);
    const polite = String(currentUser?.id) < key;
    const pc = new RTCPeerConnection({ iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: ['turn:www.jv-games.com:3478', 'turn:www.jv-games.com:3478?transport=tcp'], username: 'jvturn', credential: 'jvTurnSecret2024' }
    ] });
    const entry = { pc, polite, makingOffer: false, ignoreOffer: false };
    peersRef.current.set(key, entry);

    // recvonly transceiver: hear remote audio immediately. The mic track is attached
    // later via addTrack on first unmute (which re-negotiates so the peer learns it).
    pc.addTransceiver('audio', { direction: 'recvonly' });
    const stream = localStreamRef.current;
    if (stream) stream.getAudioTracks().forEach(t => pc.addTrack(t, stream));

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(peerId, { type: 'candidate', candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      let audio = audioRef.current.get(key);
      if (!audio) {
        audio = document.createElement('audio');
        audio.autoplay = true;
        audio.playsInline = true;
        audio.style.display = 'none';
        document.body.appendChild(audio);
        audioRef.current.set(key, audio);
      }
      audio.muted = !speakerOn;
      // Robust: use the attached stream; fall back to a fresh MediaStream for the track.
      const stream = e.streams && e.streams[0] ? e.streams[0] : new MediaStream([e.track]);
      if (audio.srcObject !== stream) audio.srcObject = stream;
      // Always play() (even while muted) so the element is "activated" — on some
      // Android WebViews a play() that only happens later fails to start.
      audio.play().then(() => console.log('[voice] play OK', key)).catch((err) => console.log('[voice] play FAIL', key, err.name));
    };
    pc.onnegotiationneeded = async () => {
      try {
        entry.makingOffer = true;
        await pc.setLocalDescription();
        sendSignal(peerId, { type: 'offer', sdp: pc.localDescription });
      } catch (e) { /* ignore */ } finally {
        entry.makingOffer = false;
      }
    };
    pc.onconnectionstatechange = () => {
      console.log('[voice] connectionState', key, pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        peersRef.current.delete(key);
      }
    };
    pc.oniceconnectionstatechange = () => {
      console.log('[voice] iceState', key, pc.iceConnectionState);
    };
    return entry;
  }, [currentUser, sendSignal, speakerOn]);

  useEffect(() => {
    if (!socket) return;
    const onSignal = async (data) => {
      const from = data?.from;
      const sig = data?.signal;
      if (!from || !sig) return;
      console.log('[voice] onSignal ←', from, sig.type);
      const key = String(from);
      let entry = peersRef.current.get(key);
      if (!entry) entry = setupPeer(from);
      const { pc } = entry;
      try {
        if (sig.type === 'offer') {
          const offerCollision = entry.makingOffer || pc.signalingState !== 'stable';
          entry.ignoreOffer = !entry.polite && offerCollision;
          if (entry.ignoreOffer) return;
          await pc.setRemoteDescription(sig.sdp);
          await pc.setLocalDescription();
          sendSignal(from, { type: 'answer', sdp: pc.localDescription });
        } else if (sig.type === 'answer') {
          await pc.setRemoteDescription(sig.sdp);
        } else if (sig.type === 'candidate') {
          if (sig.candidate) await pc.addIceCandidate(sig.candidate);
        }
      } catch (e) { /* ignore */ }
    };
    socket.on('custom-voice-signal', onSignal);
    return () => socket.off('custom-voice-signal', onSignal);
  }, [socket, setupPeer, sendSignal]);

  useEffect(() => {
    if (!room || !room.players || !currentUser) return;
    const others = room.players.filter(p => String(p.userId) !== String(currentUser.id));
    others.forEach(p => setupPeer(p.userId));
  }, [room, currentUser, setupPeer]);

  // Cleanup peers + stop mic on unmount.
  useEffect(() => () => {
    peersRef.current.forEach(entry => entry.pc.close());
    peersRef.current.clear();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
    }
  }, []);

  const playerCount = room?.players?.length || 0;
  const maxPlayers = room?.maxPlayers || 0;
  const canStart = isCreator && playerCount >= 2 && !starting;

  return (
    <div className="crl-page">
      <div className="crl-header">
        <button className="crl-back" onClick={handleLeave}>← Leave</button>
        <h1>🎪 Custom Room</h1>
        <span className={`crl-status ${isConnected ? 'ok' : ''}`}>
          {isConnected ? '● Connected' : '○ Connecting…'}
        </span>
      </div>

      {/* Room code */}
      <div className="crl-code-card">
        <span className="crl-code-label">Share this code with your friends</span>
        <div className="crl-code-row">
          <span className="crl-code">{room?.roomCode || code || '—'}</span>
          <button className="crl-copy-btn" onClick={copyCode}>
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      </div>

      {/* Room info */}
      <div className="crl-info">
        <div className="crl-info-item"><span>💰 Entry</span><strong>${room?.amount || '—'}</strong></div>
        <div className="crl-info-item"><span>🎯 Points</span><strong>{room?.points || '—'}</strong></div>
        <div className="crl-info-item"><span>👥 Players</span><strong>{playerCount}/{maxPlayers}</strong></div>
      </div>

      {/* Voice controls */}
      <div className="crl-voice">
        <span className="crl-voice-label">🎙️ Voice Chat</span>
        <div className="crl-voice-btns">
          <button
            className={`crl-voice-btn ${micOn ? 'on' : 'off'}`}
            onClick={() => setMicEnabled(!micOn)}
          >
            🎤 {micOn ? 'Mic On' : 'Mic Muted'}
          </button>
          <button
            className={`crl-voice-btn ${speakerOn ? 'on' : 'off'}`}
            onClick={() => setSpeakerEnabled(!speakerOn)}
          >
            🔊 {speakerOn ? 'Speaker On' : 'Speaker Muted'}
          </button>
        </div>
      </div>

      {/* Players */}
      <div className="crl-players">
        <h3>Players</h3>
        {(!room?.players || room.players.length === 0) ? (
          <p className="crl-empty">Waiting for players…</p>
        ) : (
          <ul className="crl-player-list">
            {room.players.map((p, i) => (
              <li key={p.userId} className="crl-player">
                <span className="crl-seat">#{i + 1}</span>
                <span className="crl-name">{p.username}</span>
                {String(p.userId) === String(room.creatorId) && <span className="crl-host">👑 Host</span>}
                {String(p.userId) === String(currentUser?.id) && <span className="crl-you">(you)</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <div className="crl-error">
          ⚠️ {error}
          {permissionDenied && (
            <button
              onClick={openAppSettings}
              style={{
                marginLeft: '8px', padding: '4px 10px', borderRadius: '4px',
                border: '1px solid #ff9800', background: 'rgba(255,152,0,0.15)',
                color: '#ff9800', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
              }}
            >
              🔧 Open Settings
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="crl-actions">
        {isCreator ? (
          <button className="crl-start-btn" onClick={handleStart} disabled={!canStart}>
            {starting ? 'Starting…' : playerCount < 2 ? `Waiting for players (${playerCount}/${maxPlayers})` : '🚀 Start Game'}
          </button>
        ) : (
          <p className="crl-wait-msg">⏳ Waiting for the host to start the game…</p>
        )}
      </div>
    </div>
  );
};

export default CustomRoomLobby;
